import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addDays, format, isValid, parseISO } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Link } from 'react-router'
import { api, type ArchivePropertyAffectedUser, type ArchivePropertyResponse } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const REASON_MAX_LENGTH = 500
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

type DialogStep = 'form' | 'affected-users'

/** admin-api error codes for POST /admin/properties/:id/archive (see client.ts's archiveProperty doc comment). */
function archiveErrorMessage(err: unknown): string {
  const e = err as { status?: number; body?: { code?: string; error?: string }; message?: string }
  const code = e?.body?.code
  switch (code) {
    case 'invalid_managed_until':
      return 'That date is not valid. Pick the day the last guest checks out.'
    case 'invalid_reason':
      return `The reason is too long. Keep it to ${REASON_MAX_LENGTH} characters or fewer.`
    case 'managed_until_before_managed_since':
      return 'That date is before this property started being managed. Pick a later date.'
    case 'cannot_restore_archived_property':
      return 'This property is already archived and cannot be archived again.'
    default:
      if (e?.status === 404) return 'This property could not be found.'
      return e?.body?.error || e?.message || 'Something went wrong. Please try again.'
  }
}

/** Generic actionable message for a user-deactivate (upsertUser) failure. */
function deactivateErrorMessage(err: unknown): string {
  const e = err as { status?: number; body?: { error?: string }; message?: string }
  return e?.body?.error || e?.message || 'Something went wrong. Please try again.'
}

/** The exclusive managed_until boundary, one day earlier -- the actual last night managed. */
function lastManagedNightLabel(managedUntil: string): string | null {
  const boundary = parseISO(managedUntil)
  if (!isValid(boundary)) return null
  return format(addDays(boundary, -1), 'MMM d, yyyy')
}

interface ArchivePropertyCardProps {
  propertyId: string
  archived?: boolean
  managedUntil?: string | null
  /** Called after a successful archive call, before any affected-users prompt. */
  onArchived?: (response: ArchivePropertyResponse) => void
}

/**
 * Property offboarding (CAP-171): a one-way "stop managing this property"
 * action. `managed_until` is EXCLUSIVE (the first day no longer managed), so
 * the dialog asks for the last guest's checkout day instead and shows the
 * derived last managed night, to keep the boundary unambiguous -- see
 * client.ts's archiveProperty doc comment for the wire contract.
 *
 * There is no unarchive flow anywhere in this system, so once archived the
 * action becomes a disabled no-op rather than something that looks
 * re-triggerable.
 */
export function ArchivePropertyCard({
  propertyId,
  archived,
  managedUntil,
  onArchived,
}: ArchivePropertyCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [step, setStep] = useState<DialogStep>('form')
  const [checkoutDate, setCheckoutDate] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // The affected-users step's own state -- kept here (rather than in a
  // separate component) since it's the same Dialog transitioning content,
  // not a second dialog. `deactivatedUserIds` lets a just-deactivated row
  // update in place instead of requiring the operator to reopen the dialog
  // to see it took effect.
  const [affectedUsers, setAffectedUsers] = useState<ArchivePropertyAffectedUser[]>([])
  const [deactivatingUserId, setDeactivatingUserId] = useState<string | null>(null)
  const [deactivatedUserIds, setDeactivatedUserIds] = useState<Set<string>>(new Set())
  const [deactivateError, setDeactivateError] = useState<string | null>(null)

  const lastManagedNight = useMemo(
    () => (ISO_DATE_RE.test(checkoutDate) ? lastManagedNightLabel(checkoutDate) : null),
    [checkoutDate]
  )

  const resetForm = () => {
    setStep('form')
    setCheckoutDate('')
    setReason('')
    setFormError(null)
    setAffectedUsers([])
    setDeactivatingUserId(null)
    setDeactivatedUserIds(new Set())
    setDeactivateError(null)
  }

  /**
   * Set the moment an archive succeeds, cleared the moment the resulting
   * refresh is issued. A ref rather than state because nothing renders from
   * it and closeDialog has to read it in the same tick handleSubmit sets it.
   */
  const refreshOwedRef = useRef(false)

  /**
   * Settles the refresh a completed archive owes. Idempotent, never awaited,
   * and its rejections are swallowed.
   *
   * Two separate facts drive this, and keeping them separate is the point.
   * The archive succeeding is what makes the cached property/list data stale
   * (one producer: refreshOwedRef, set in handleSubmit). Paying it has to
   * wait, because a refetch failure flips a live PropertyDetailsPage's
   * useProperty observer to its error state and unmounts this card -- taking
   * an unread affected-users prompt with it, for a property that is already
   * irreversibly archived.
   *
   * What it waits for is either terminal moment below, and the two are
   * exhaustive: this card closes its dialog, or it goes away. Codex review
   * rounds 3, 4, 5 and 6 were four different paths between the archive and a
   * payment that never happened; tying payment to those two moments rather
   * than to each exit path is what ends the series.
   *
   * Accepted limitation: if the route unmounts while the POST itself is
   * still in flight, the debt is recorded after this cleanup has run, so no
   * invalidation is issued and the affected_users list is not shown. Not
   * defended against deliberately. It needs a browser Back inside the
   * sub-second window after a deliberate confirm click (Escape and outside
   * clicks are already blocked while submitting), on an action taken a
   * handful of times a year. Both consequences are mild and recoverable: the
   * cache is stale for at most this query's 30s staleTime, and the list is
   * just "who is assigned to this property", visible on the users page --
   * archiving never deactivates anyone on its own, so nothing has silently
   * happened to an account. Closing it needs a route-independent mutation
   * lifecycle, which is more machinery than the feature it protects.
   */
  const payRefreshDebt = useCallback(() => {
    if (!refreshOwedRef.current) return
    refreshOwedRef.current = false
    queryClient.invalidateQueries({ queryKey: ['property', propertyId] }).catch(() => {})
    queryClient.invalidateQueries({ queryKey: ['properties'] }).catch(() => {})
  }, [queryClient, propertyId])

  // Terminal moment two: the card is gone -- what following one of the
  // prompt's own affected-user links does, closing nothing. Safe for exactly
  // the reason the deferral exists: there is nothing left on screen for a
  // refetch failure to take down.
  useEffect(() => payRefreshDebt, [payRefreshDebt])

  /**
   * Terminal moment one, and the single close path: every dismissal --
   * Cancel, Done, Escape, an outside click, or handleSubmit's own
   * no-affected-users close -- goes through here.
   *
   * Resetting is unconditionally safe here: the property is either not
   * archived at all (form step) or already archived (affected-users step,
   * where there is nothing left to reopen for), so no unsaved state is lost.
   */
  const closeDialog = () => {
    setDialogOpen(false)
    resetForm()
    payRefreshDebt()
  }

  const handleOpenChange = (open: boolean) => {
    // Closing while a request is in flight would discard its eventual
    // result, including a failure the operator never gets to see.
    if (submitting || deactivatingUserId) return
    if (open) {
      setDialogOpen(true)
      return
    }
    closeDialog()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ISO_DATE_RE.test(checkoutDate)) {
      setFormError('Pick the day the last guest checks out.')
      return
    }
    if (reason.length > REASON_MAX_LENGTH) {
      setFormError(`Reason must be ${REASON_MAX_LENGTH} characters or fewer.`)
      return
    }
    setSubmitting(true)
    setFormError(null)
    try {
      const response = await api.archiveProperty(propertyId, {
        managed_until: checkoutDate,
        reason: reason.trim() || undefined,
      })
      // The one place a refresh is ever owed from. payRefreshDebt settles
      // it; see its comment for why the two are kept apart.
      refreshOwedRef.current = true
      onArchived?.(response)
      if (response.affected_users.length > 0) {
        // Skip the "archived" toast here -- the affected-users step itself
        // is the confirmation the operator sees next, and deactivating from
        // it (or not) is the real decision point still ahead of them.
        setAffectedUsers(response.affected_users)
        setStep('affected-users')
      } else {
        toast({
          title: 'Property archived',
          description: `Marked not managed starting ${format(parseISO(checkoutDate), 'MMM d, yyyy')}.`,
        })
        // Not handleOpenChange -- `submitting` is still true until this
        // function's finally block, and its in-flight guard would swallow
        // the close.
        closeDialog()
      }
    } catch (err: unknown) {
      setFormError(archiveErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Deactivating a user reuses the existing user-status endpoint
   * (api.upsertUser), never a new one -- see CAP-171's investigation notes.
   * upsertUser is keyed by email, and first_name/last_name/role are all
   * optional on the request: admin-api's handler treats an omitted field as
   * "leave unchanged", not "clear it" (the same distinction user-details.tsx's
   * saveUser relies on for names), so sending only email + status here is the
   * smallest correct call. patchUser -- the narrower single-field endpoint
   * used elsewhere on this page for e.g. notify_new_reports_published --
   * doesn't cover `status` at all (see PatchUserRequestSchema in
   * @natalan/capehost-contracts), so it isn't an option here.
   *
   * This is never called automatically -- only an explicit per-row click,
   * per root CLAUDE.md's "system never auto-sends"-style rule applied to
   * account changes: archiving a property never implies deactivating anyone.
   */
  const handleDeactivate = async (user: ArchivePropertyAffectedUser) => {
    setDeactivatingUserId(user.user_id)
    setDeactivateError(null)
    try {
      await api.upsertUser({ email: user.email, status: 'disabled' })
      setDeactivatedUserIds(prev => new Set(prev).add(user.user_id))
      toast({
        title: 'User deactivated',
        description: `${user.email} was disabled.`,
      })
    } catch (err: unknown) {
      setDeactivateError(`Could not deactivate ${user.email}: ${deactivateErrorMessage(err)}`)
    } finally {
      setDeactivatingUserId(null)
    }
  }

  return (
    <Card className="border-danger/30 bg-surface2/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-danger">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Archive property
        </CardTitle>
        <CardDescription>
          Stop managing this property. This does not touch Uplisting or delete any data here -- it
          only marks this property as no longer part of the active portfolio. There is no way to
          undo this from this screen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {archived ? (
          <div className="space-y-2">
            <Badge variant="secondary">Archived</Badge>
            <p className="text-sm text-textMuted">
              This property was archived
              {managedUntil ? (
                <>
                  {' '}
                  -- not managed starting {format(parseISO(managedUntil), 'MMM d, yyyy')} (last
                  managed night: {lastManagedNightLabel(managedUntil)}).
                </>
              ) : (
                '.'
              )}{' '}
              There is no unarchive flow, so this action is no longer available.
            </p>
          </div>
        ) : (
          <Button type="button" variant="destructive" onClick={() => setDialogOpen(true)}>
            Archive property…
          </Button>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          {step === 'form' ? (
            <>
              <DialogHeader>
                <DialogTitle>Archive this property?</DialogTitle>
                <DialogDescription>
                  This stops managing the property from that day forward. Pick the day the last
                  guest checks out.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="archive-checkout-date">When does the last guest check out?</Label>
                  <Input
                    id="archive-checkout-date"
                    type="date"
                    value={checkoutDate}
                    disabled={submitting}
                    onChange={e => setCheckoutDate(e.target.value)}
                    required
                  />
                  {lastManagedNight && (
                    <p className="text-xs text-textMuted">
                      This property will be marked not managed starting{' '}
                      {format(parseISO(checkoutDate), 'MMM d, yyyy')}. Last managed night:{' '}
                      <span className="font-medium text-text">{lastManagedNight}</span>.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="archive-reason">Reason (optional)</Label>
                  <textarea
                    id="archive-reason"
                    value={reason}
                    disabled={submitting}
                    onChange={e => setReason(e.target.value)}
                    maxLength={REASON_MAX_LENGTH}
                    rows={3}
                    placeholder="e.g. Owner sold the property"
                    className="flex w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text transition-all placeholder:text-textFaint focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <p className="text-xs text-textMuted">
                    {reason.length}/{REASON_MAX_LENGTH}
                  </p>
                </div>

                {formError && (
                  <div className="rounded-lg border border-border bg-surface2 p-3 text-sm text-destructive">
                    {formError}
                  </div>
                )}

                <DialogFooter>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={submitting}
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="destructive" disabled={submitting}>
                    {submitting ? 'Archiving…' : 'Archive property'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Property archived</DialogTitle>
                <DialogDescription>
                  {affectedUsers.some(u => u.left_with_zero_active_properties)
                    ? 'This leaves some users with no active property. Deactivate anyone who no longer needs portal access, or skip this and handle it later.'
                    : 'These users had this property assigned. None are left without another active property, so no action is required here.'}
                </DialogDescription>
              </DialogHeader>

              <ul className="max-h-96 space-y-3 overflow-y-auto">
                {affectedUsers.map(user => {
                  const isZeroActive = user.left_with_zero_active_properties
                  const isDeactivated = deactivatedUserIds.has(user.user_id)
                  const isDeactivating = deactivatingUserId === user.user_id
                  return (
                    <li
                      key={user.user_id}
                      className={`rounded-lg border p-3 text-sm ${
                        isZeroActive
                          ? 'border-amber/40 bg-warningBg'
                          : 'border-border bg-surface2/60'
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          {deactivatingUserId !== null ? (
                            // Navigating away while ANY deactivate is pending
                            // (not just this row's) would unmount this
                            // dialog and silently discard that request's
                            // eventual result -- the same lost-error
                            // scenario the Done fix above addresses (Codex
                            // PR review round 2).
                            <span className="font-medium text-text">{user.email}</span>
                          ) : (
                            <Link
                              to={`/users/${user.user_id}`}
                              className="font-medium text-text underline-offset-4 hover:underline"
                            >
                              {user.email}
                            </Link>
                          )}
                          {isZeroActive && !isDeactivated && (
                            <Badge variant="warning" className="ml-2">
                              No active properties left
                            </Badge>
                          )}
                          {isDeactivated && (
                            <Badge variant="success" className="ml-2 gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Deactivated
                            </Badge>
                          )}
                          <div className="mt-1 text-xs text-textMuted">
                            {user.other_properties.length === 0 ? (
                              <span>No other properties assigned.</span>
                            ) : (
                              <span>
                                Other properties:{' '}
                                {user.other_properties
                                  .map(p => `${p.display_name}${p.archived ? ' (archived)' : ''}`)
                                  .join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        {isZeroActive && !isDeactivated && (
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            disabled={isDeactivating || deactivatingUserId !== null}
                            onClick={() => handleDeactivate(user)}
                          >
                            {isDeactivating ? 'Deactivating…' : 'Deactivate'}
                          </Button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>

              {deactivateError && (
                <div className="rounded-lg border border-border bg-surface2 p-3 text-sm text-destructive">
                  {deactivateError}
                </div>
              )}

              <DialogFooter>
                {/*
                  No toast or message of its own: any deactivation that
                  actually happened already got its own toast at the moment
                  it happened, and ending this step (or skipping it) must
                  not imply anything more.
                */}
                <Button
                  type="button"
                  variant="secondary"
                  disabled={deactivatingUserId !== null}
                  onClick={() => handleOpenChange(false)}
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  )
}
